import { useContentLink } from "@robe/client/xcore-ui";
import { Box, Collapse, Flex, Icon, Link, Text, TextProps } from "@xcorejs/ui";
import LocaleSwitcherIcon12 from "components/Icons/12/locale-switcher.svg";
import LocaleSwitcherIcon20 from "components/Icons/20/locale-switcher.svg";
import DropIcon8 from "components/Icons/8/drop.svg";
import { useEffect, useState } from "react";
import { useLayout } from "xcore";
import { ActiveLanguageStyle } from "./DomainSwitcher";
import { SubNavLinkStyle } from "./NavItem";
import { useDomainSwitcher } from "./useDomainSwitcher";
import { useRouter } from "next/router";

interface MobileDomainSwitcherProps {
  open: boolean;
  setState: (open: boolean) => void;
  mode: "bar" | "icon";
  isLogged?: boolean;
}

const MobileDomainSwitcher = ({ open, setState, mode, isLogged }: MobileDomainSwitcherProps) => {
  const { locale } = useRouter();
  const { core, unavailableLocalePage } = useLayout();
  const [ssrHide, setSsrHide] = useState(true);
  const { dropdownLinkProps, pageContent } = useDomainSwitcher();
  const { as } = useContentLink();

  useEffect(() => void setSsrHide(false), []);

  return (
    <Flex
      display={{ _: "flex", md: "none" }}
      onClick={() => setState(!open)}
      {...(isLogged && { marginTop: { sm: "3.2rem", md: "0" } })}
      {...(mode === "bar"
        ? {
          backgroundColor: "robe",
          justifyContent: "center",
          alignItems: "center",
          height: "3.3rem",
          width: "100%",
          position: "relative",
          zIndex: 1004,
        }
        : { height: "2rem" })}
    >
      <Box position="relative" display="flex" alignItems="center" onClick={() => setState(!open)}>
        <Icon
          svg={mode === "bar" ? <LocaleSwitcherIcon12 /> : <LocaleSwitcherIcon20 />}
          fill={mode === "bar" ? "white" : open ? "robe" : "gray"}
          mr="0.5rem"
          height={mode === "bar" ? "1.4rem" : "2rem"}
        />

        {mode === "bar" && (
          <>
            <Text width="100%" color="white" fontSize="1.3rem" fontWeight={600}>
              {core.currentLocaleConfig.mobileDomainSwitcherLabel}
            </Text>

            <Icon svg={<DropIcon8 />} fill="white" transition="transform 300ms" ml="0.65rem" transform={open ? "rotate(180deg)" : "none"} />
          </>
        )}
      </Box>

      {!ssrHide && (
        <Collapse
          open={open}
          position="absolute"
          zIndex="1005"
          left="0"
          top={mode === "bar" ? "3.3rem" : "5rem"}
          width="100%"
          background="rgba(0,0,0,0.5)"
          height={mode === "bar" ? "100%" : "calc(100vh - 5rem)"}
        >
          {dropdownLinkProps.map((link, i) => {
            let href = pageContent?.slug ? as(pageContent, { locale: link.locale }) : null;

            // If page content is not available for locale redirect to according unavailable locale page
            if (!href) {
              href = as(unavailableLocalePage, { locale: link.locale }) ?? "/unavailable";
            }

            const linkTextStyle =
              link.locale === locale
                ? mode === "bar"
                  ? { ...ActiveLangBarModeStyle }
                  : { ...ActiveLanguageStyle }
                : mode === "bar"
                  ? { color: "white" }
                  : {};

            return (
              <Link
                key={i}
                href={href}
                type="simple"
                {...SubNavLinkStyle}
                position="relative"
                width="100%"
                {...(i === 0 && { pt: "2rem" })}
                {...(i === dropdownLinkProps.length - 1 && { pb: "2rem" })}
                px="2rem"
                justifyContent="space-between"
                background={mode === "bar" ? "robe" : "white"}
              >
                <Text lineHeight="2.1rem" {...linkTextStyle}>
                  {link.label}
                </Text>
                <Text fontWeight={600} lineHeight="2.1rem" {...linkTextStyle}>
                  {link.locale.split("-")[0].toUpperCase()}
                </Text>
              </Link>
            );
          })}
        </Collapse>
      )}
    </Flex>
  );
};

export default MobileDomainSwitcher;

export const ActiveLangBarModeStyle: TextProps = {
  color: "black",
  opacity: 1,

  _hover: {
    color: "black",
    bg: "#ee0037",
  },
};
